/**
 * 플랫폼 감지 유틸리티
 * 웹 브라우저에서 실행 중인지, 네이티브 앱에서 실행 중인지 구분
 */

/**
 * 웹 브라우저에서 실행 중인지 확인
 * @returns true: 웹 브라우저, false: 네이티브 앱
 */
export const isWeb = (): boolean => {
  // window 객체가 없으면 앱 환경 (또는 SSR 환경)
  if (typeof window === 'undefined') {
    return false
  }

  // 네이티브 브리지가 있으면 앱 환경
  // - Capacitor: 하이브리드 앱 프레임워크
  // - ReactNativeWebView: React Native WebView
  // - webkit.messageHandlers: iOS WKWebView
  // - Android: Android WebView의 특정 객체들
  // - FlutterBridge: Flutter WebView (네이티브가 onPageFinished에서 "늦게" 주입)
  // - FlutterChannel: Flutter JavaScriptChannel (문서 시작 시점에 주입돼 FlutterBridge보다 이르다).
  //   앱 시작 직후 실행되는 코드(immersive-pad 토글·FCM 등록 등)가 FlutterBridge만 보면
  //   아직 미주입이라 isApp()=false로 오판했다 → 딥링크 콜드스타트 레이아웃 쏠림/토큰 미등록 버그.
  if (
    (window as any).Capacitor ||
    (window as any).ReactNativeWebView ||
    (window as any).webkit?.messageHandlers ||
    (window as any).Android ||
    (window as any).FlutterBridge ||
    (window as any).FlutterChannel
  ) {
    return false
  }

  // 네이티브 브리지가 없으면 웹 브라우저로 간주
  // (PWA standalone 모드도 웹 기반이므로 웹으로 처리)
  return true
}

/**
 * 네이티브 앱에서 실행 중인지 확인
 * @returns true: 네이티브 앱, false: 웹 브라우저
 */
export const isApp = (): boolean => {
  return !isWeb()
}

/**
 * iOS 플랫폼인지 확인
 * @returns true: iOS, false: 그 외
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  // User-Agent로 iOS 확인
  const userAgent = window.navigator.userAgent.toLowerCase()
  const isIOSUserAgent =
    /iphone|ipad|ipod/.test(userAgent) ||
    (userAgent.includes('mac') && 'ontouchend' in document)

  // iOS WebView 확인
  const isIOSWebView = !!(window as any).webkit?.messageHandlers

  return isIOSUserAgent || isIOSWebView
}

/**
 * 메인 페이지 진입 시 이전 히스토리 정리
 * 웹 환경에서만 동작하며, 현재 페이지를 히스토리의 시작점으로 설정
 * 인증 관련 페이지(/, /login, /register, /contact-sync 등)의 히스토리를 제거
 */
export const clearAuthHistory = (): void => {
  if (!isWeb()) {
    return
  }

  const currentPath = window.location.pathname

  // 메인 페이지인지 확인
  const isMainPage = currentPath.startsWith('/main-')

  if (isMainPage) {
    // sessionStorage에 메인 진입 플래그 확인 (한 번만 실행되도록)
    const historyClearedKey = `history_cleared_${currentPath}`
    const hasCleared = sessionStorage.getItem(historyClearedKey)

    if (!hasCleared) {
      // 현재 URL을 히스토리의 시작점으로 만들기
      // replaceState를 사용하여 현재 페이지를 히스토리의 첫 번째 엔트리로 설정
      // 이렇게 하면 뒤로가기 시 이전 인증 페이지로 돌아가지 않음
      window.history.replaceState(
        { ...window.history.state, mainEntry: true, path: currentPath },
        '',
        currentPath
      )

      // 플래그 설정 (각 메인 페이지별로 관리)
      sessionStorage.setItem(historyClearedKey, 'true')
    }
  }
}
